import { useEffect, useState } from 'react';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DosenButton from '../../../../../../../../components/ui/Buttons/DosenButton';

interface ReviewModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * Confirms a Program Design save. Saving here never publishes or invites
 * anyone, so the copy says who can see the change instead of warning about it.
 */
export function ReviewModal({ open, onClose, onConfirm }: ReviewModalProps) {
  // One save per opening: a quick double click must not save twice.
  const [confirmed, setConfirmed] = useState(false);
  useEffect(() => {
    if (open) setConfirmed(false);
  }, [open]);

  const handleConfirm = () => {
    if (confirmed) return;
    setConfirmed(true);
    onConfirm();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: 480,
          bgcolor: '#0F0F0F',
          border: '1px solid #444444',
          borderRadius: 2,
          overflow: 'hidden'
        }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: 3,
          py: 2,
          borderBottom: '1px solid #2A2A2A'
        }}
      >
        <Typography sx={{ color: '#FDFDFD', fontSize: 18, fontWeight: 700 }}>Save changes?</Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close" sx={{ color: '#FDFDFD' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ px: 3, pt: 2, pb: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Typography sx={{ color: '#B6B6B6', fontSize: 14, lineHeight: 1.5 }}>
          Your edits will be saved to this program. Only learners who are enrolled can see it, and they'll see the
          updated content the next time they open it.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <DosenButton tone="basic" fullWidth onClick={onClose}>
            Continue Reviewing
          </DosenButton>
          <DosenButton fullWidth onClick={handleConfirm} disabled={confirmed}>
            Save Changes
          </DosenButton>
        </Box>
      </Box>
    </Dialog>
  );
}
